import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Check, Close, FileDownloadOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { Code } from '~/components/Common/Code';
import PageHeader from '~/components/Common/Elements/PageHeader';
import ErrorDisplay from '~/components/Common/ErrorDisplay';

import { formatDateOrdinalWithClock } from '~/services/TimeService';
import cruiseService, { BookingStepsByBookingIdResponse } from '~/services/cruises/BookingInfoService';

type BookingStep = BookingStepsByBookingIdResponse['result'][0];

const bookingStepExportUrlRevocationDelayMs = 1000;

const getBookingStepExportFilename = (step: string): string => {
  const normalizedStep = step
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `${normalizedStep || 'booking_log'}.json`;
};

export default function BookingLogsPage() {
  const params = useParams<{ booking_id: string }>();

  const bookingId = params.booking_id;

  const [bookingSteps, setBookingSteps] = useState<Array<BookingStep> | null>(null);
  const [bookingStep, setBookingStep] = useState<BookingStep | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getBookingSteps = useCallback(
    async (bookingId: string): Promise<void> => {
      try {
        const response = await cruiseService.getBookingStepsByBookingId(bookingId);

        if (!response || !response.result?.length) {
          setError('No booking logs found');

          return;
        }

        setBookingSteps(response.result);
      } catch (error) {
        setError(error.message);
      }
    },
    [bookingId],
  );

  const getBookingStepIcon = useCallback((bookingStep: BookingStep) => {
    const status = bookingStep.revelexResponse.status as number | null | undefined;
    const error = bookingStep.revelexResponse?.['data']?.['error'] as Record<string, unknown> | undefined;

    return status && status >= 200 && status <= 299 && !error ? <Check color="success" /> : <Close color="error" />;
  }, []);

  const exportBookingStep = useCallback((bookingStep: BookingStep) => {
    const file = new Blob(
      [
        JSON.stringify(
          {
            request: bookingStep.revelexRequest,
            response: bookingStep.revelexResponse,
          },
          null,
          2,
        ),
      ],
      { type: 'application/json' },
    );
    const downloadUrl = URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = downloadUrl;
    link.download = getBookingStepExportFilename(bookingStep.step);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), bookingStepExportUrlRevocationDelayMs);
  }, []);

  const [collapseRequest, setCollapseRequest] = useState(true);

  const toggleCollapseRequest = useCallback(() => {
    setCollapseRequest((prev) => !prev);
  }, []);

  const [collapseResponse, setCollapseResponse] = useState(true);

  const toggleCollapseResponse = useCallback(() => {
    setCollapseResponse((prev) => !prev);
  }, []);

  useEffect(() => {
    getBookingSteps(bookingId);
  }, [bookingId, getBookingSteps]);

  const revelexRequestJson = useMemo(() => {
    return JSON.stringify(bookingStep?.revelexRequest || {}, null, 2);
  }, [bookingStep?.revelexRequest]);

  const revelexResponseJson = useMemo(() => {
    return JSON.stringify(bookingStep?.revelexResponse || {}, null, 2);
  }, [bookingStep?.revelexResponse]);

  return (
    <Container maxWidth="xl">
      <PageHeader title="Booking Logs" backButton="/cruises/booking-logs" />
      {error ? (
        <ErrorDisplay message={error} />
      ) : (
        <Stack direction="column" spacing={2}>
          {bookingSteps && bookingSteps.length > 0 && (
            <Box sx={{ display: 'grid' }}>
              <Grid container spacing={1} columns={8}>
                <Grid item xs={2} boxShadow={1}>
                  <Typography variant="overline" fontSize={24} p={2} gutterBottom>
                    Booking Steps
                  </Typography>
                </Grid>
                <Grid item xs={6} boxShadow={1}>
                  <Typography variant="overline" fontSize={24} p={2} gutterBottom>
                    {(bookingStep ? bookingStep.step + ' ' : '') + 'Request / Response'}
                  </Typography>
                </Grid>
                <Grid item xs={2} boxShadow={1}>
                  <Box position="sticky" top="0">
                    {bookingSteps.map((_bookingStep, index) => (
                      <Card
                        key={_bookingStep.id}
                        sx={{
                          bgcolor: _bookingStep.id === bookingStep?.id ? 'lightcyan' : 'unset',
                          borderBottom: index < bookingSteps.length - 1 ? 1 : 0,
                          borderColor: 'divider',
                          marginRight: '8px',
                          paddingRight: '1px',
                        }}
                      >
                        <CardContent>
                          <CardActionArea onClick={() => setBookingStep(_bookingStep)}>
                            <Stack alignItems="center" direction="row" gap={2}>
                              <Typography variant="overline" fontSize={16} gutterBottom noWrap>
                                {`${index + 1}. ${_bookingStep.step}`}
                              </Typography>
                              {getBookingStepIcon(_bookingStep)}
                            </Stack>
                          </CardActionArea>
                          <Stack alignItems="center" direction="row" justifyContent="space-between">
                            <Stack alignItems="flex-start" direction="row">
                              <Typography variant="body2" color="text.secondary">
                                {formatDateOrdinalWithClock(_bookingStep.createdAt)}
                              </Typography>
                            </Stack>
                            <Tooltip title="Export log">
                              <IconButton
                                aria-label={`Export ${_bookingStep.step} log`}
                                size="small"
                                onClick={() => exportBookingStep(_bookingStep)}
                              >
                                <FileDownloadOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Grid>
                <Grid item xs={6} boxShadow={1}>
                  {!bookingStep && (
                    <Typography variant="overline" fontSize={16} display="block" p={2} gutterBottom>
                      Nothing to show
                    </Typography>
                  )}
                  {bookingStep && (
                    <Box component="main" p={2}>
                      <Box component="span">
                        <Typography variant="overline" fontSize={16} display="block" gutterBottom>
                          Revelex Request
                        </Typography>
                        <Button onClick={toggleCollapseRequest} variant="outlined" size="small">
                          {collapseRequest ? 'Expand All' : 'Collapse All'}
                        </Button>
                        <Code value={revelexRequestJson} lang="json" />
                      </Box>
                      <Box component="span">
                        <Typography variant="overline" fontSize={16} display="block" gutterBottom>
                          Revelex Response
                        </Typography>
                        <Button onClick={toggleCollapseResponse} variant="outlined" size="small">
                          {collapseResponse ? 'Expand All' : 'Collapse All'}
                        </Button>
                        <Code value={revelexResponseJson} lang="json" />
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </Stack>
      )}
    </Container>
  );
}
